import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSDocVerificationCheckStatusInput,
  type EnrollmentHYCSDocumentVerificationData,
  type EnrollmentHYCSDocumentVerificationSubmitInput,
  type EnrollmentHYCSDocumentVerificationSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDocumentVerificationData>
 * @example const response = await getEnrollmentHYCSDocumentVerificationData()
 */
export const getEnrollmentHYCSDocumentVerificationData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSDocumentVerificationData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSDocumentVerificationData',
      query: `mutation getEnrollmentHYCSDocumentVerificationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              documentVerificationStatus
              documentVerificationUrl
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDocumentVerificationData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSDocumentVerificationData = createQueryHook<
  undefined,
  EnrollmentHYCSDocumentVerificationData
>(getEnrollmentHYCSDocumentVerificationData)

/**
 * Submit Enrollment HYCS DocumentVerification data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentHYCSDocumentVerificationData();
 */
export const submitEnrollmentHYCSDocumentVerificationData = async (
  enrollmentDocumentVerificationSubmitInput: EnrollmentHYCSDocumentVerificationSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSDocumentVerificationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSDocumentVerificationData',
      query: `mutation submitEnrollmentHYCSDocumentVerificationData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              documentVerificationUrl
              documentVerificationStatus
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocumentVerificationSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDocumentVerificationSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSDocumentVerificationData = createMutationHook<
  EnrollmentHYCSDocumentVerificationSubmitInput,
  EnrollmentHYCSDocumentVerificationSubmitOutput
>(submitEnrollmentHYCSDocumentVerificationData)

/**
 * Submit Enrollment HYCS DocumentVerificationCheckStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSDocumentVerificationSubmitOutput>
 * @example const response = await submitEnrollmentHYCSDocVerificationCheckStatusData();
 */
export const submitEnrollmentHYCSDocVerificationCheckStatusData = async (
  enrollmentDocVerificationCheckStatusInput: EnrollmentHYCSDocVerificationCheckStatusInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSDocumentVerificationSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSDocVerificationCheckStatusData',
      query: `mutation submitEnrollmentHYCSDocVerificationCheckStatusData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              documentVerificationStatus
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentDocVerificationCheckStatusInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSDocumentVerificationSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSDocVerificationCheckStatusData = createMutationHook<
  EnrollmentHYCSDocVerificationCheckStatusInput,
  EnrollmentHYCSDocumentVerificationSubmitOutput
>(submitEnrollmentHYCSDocVerificationCheckStatusData)
